import React, { FC, useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import Icon from '@components/common/assets';
import ExperienceTab from '@components/profile/profileOverlay/overlays/experience/ExperienceTab';
import ActivityForm from '@components/profile/profileOverlay/overlays/experience/experienceWrite/activityForm';
import ForeignExperienceForm from '@components/profile/profileOverlay/overlays/experience/experienceWrite/foreignExperienceForm';
import OtherActivity from '@components/profile/profileOverlay/overlays/experience/experienceWrite/etcExperienceForm';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfileExperience, { useSaveProfileExperience, viewKeys } from '@repository/profiles/useProfileExperience';
import experienceFormSchema from '@components/profile/profileOverlay/overlays/experience/experienceFormSchema';
import ProfileExperienceFormVO from '@domain/vo/profile/ProfileExperienceFormVO';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from './experience.module.scss';
const cx = classnames.bind(styles);

interface IExperienceOverlay {
  visible: boolean;
  onClose: () => void;
}

const experienceDefaultValues: RecursivePartial<ProfileExperienceFormVO> = {
  activities: [],
  foreignExperiences: [],
  experiences: [],
};

export type ExperienceTabType = 'ACTIVITY' | 'OVERSEAS' | 'ETC_EXPERIENCE';

const ExperienceOverlay: FC<IExperienceOverlay> = ({ visible, onClose }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const { data: userInfo } = useUserInfo();
  const { data: profileExperience } = useProfileExperience(!!userInfo?.id && visible);

  const { mutateAsync: saveProfileExperienceMutateAsync } = useSaveProfileExperience();

  const [selectedTabType, setSelectedTabType] = useState<ExperienceTabType>('ACTIVITY');
  const isSelectedTab = (experienceTabType: ExperienceTabType) => experienceTabType === selectedTabType;
  const [exitModalVisible, setExitModalVisible] = useState(false);

  const methods = useForm<ProfileExperienceFormVO>({
    mode: 'onBlur',
    defaultValues: experienceDefaultValues as ProfileExperienceFormVO,
    resolver: yupResolver(experienceFormSchema) as any,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onCloseOverlay = () => {
    if (isDirty) {
      setExitModalVisible(true);
      return;
    }

    onClose();
  };

  const onSubmit: SubmitHandler<ProfileExperienceFormVO> = (data) => {
    saveProfileExperienceMutateAsync(Object.assign(data, { sourcePage: query.source_page }))
      .then(async (result) => {
        if (result) {
          await queryClient.refetchQueries(viewKeys, { active: true, exact: true });
          onClose();
          Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
        } else {
          Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
        }
      })
      .catch(() => {
        Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
      });
  };

  const onSubmitError: SubmitErrorHandler<ProfileExperienceFormVO> = (errors) => {
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    const errorsKey = Object.keys(errors);

    if (errorsKey.includes('activities')) {
      setSelectedTabType('ACTIVITY');
      return;
    }
    if (errorsKey.includes('foreignExperiences')) {
      setSelectedTabType('OVERSEAS');
      return;
    }
    if (errorsKey.includes('experiences')) {
      setSelectedTabType('ETC_EXPERIENCE');
      return;
    }
  };

  useEffect(() => {
    if (profileExperience) {
      reset(new ProfileExperienceFormVO(profileExperience));
    }
  }, [profileExperience]);

  useEffect(() => {
    if (visible) {
      setSelectedTabType('ACTIVITY');
    }
  }, [visible]);

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea')}>
                {/* 상단 탭 영역 */}
                <ExperienceTab selectedTabType={selectedTabType} setSelectedTabType={setSelectedTabType} />

                <strong className={cx('overlayTitle')}>나만의 멋진 경험을 입력해주세요.</strong>
                <p className={cx('desc')}>
                  <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                  경험을 통해 얻은 결과와 배운 점을 구체적으로 작성해주세요.
                </p>
              </div>

              <div className={cx('scrollArea')}>
                <div className={cx('scrollInner')}>
                  <div className={cx('scrollWrap')}>
                    {/* 대내외 활동 */}
                    <ActivityForm visible={isSelectedTab('ACTIVITY')} />

                    {/* 해외 경험 */}
                    <ForeignExperienceForm visible={isSelectedTab('OVERSEAS')} />

                    {/* 기타 경험 */}
                    <OtherActivity visible={isSelectedTab('ETC_EXPERIENCE')} />
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button className={cx('btn', 'confirm')} type="submit">
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModalVisible} onClose={() => setExitModalVisible(false)} onExit={onClose} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default ExperienceOverlay;
